import PropTypes from 'prop-types'
import React, { Component } from 'react'
import parse from 'url-parse'
import classnames from 'classnames'
import { connect } from 'react-redux'
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup'
import { FormattedMessage } from 'react-intl'
import { SubmissionError } from 'redux-form'
import LoginForm from './LoginForm'
import styles from './_login.css'
import { Loader } from 'components/util'
import * as auth from 'services/auth/selectors'
import * as bootupSelectors from 'services/bootup/selectors'
import * as authActions from 'services/auth/actions'
import * as site from 'services/site/selectors'
import * as landingActions from 'services/landing/actions'
import FacebookLoginButton from './FacebookLoginButton'
import WechatLoginButton from './WechatLoginButton'

const mapStateToProps = (state) => ({
  appConfig: bootupSelectors.selectAppConfig(state),
  error: auth.selectError(state),
  loggingIn: auth.selectLoggingIn(state),
  dev: site.selectDev(state),
})

const mapDispatchToActions = {
  ...authActions,
  ...landingActions,
}

export class Login extends Component {

  static propTypes = {
    appConfig: PropTypes.object,
    leftAlign: PropTypes.bool,
    loggingIn: PropTypes.bool,
    loginUser: PropTypes.func.isRequired,
    onClickSignup: PropTypes.func.isRequired,
    onClickForgotPassword: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func.isRequired,
    clearLegacy: PropTypes.func.isRequired,
    dev: PropTypes.bool,
    reload: PropTypes.bool,
    signUp: PropTypes.func,
  };

  static defaultProps = {
    reload: false,
    leftAlign: false,
  };

  constructor (props) {
    super(props)
    this.setLoginError = this.setLoginError.bind(this)
    this.setLoginSubmitting = this.setLoginSubmitting.bind(this)
    this.setLoginUserError = this.setLoginUserError.bind(this)
  }

  state = {
    submitting: false,
    error: null,
  }

  componentWillMount () {
    this.props.clearLegacy()
  }

  setLoginSubmitting = (submitting) => {
    this.setState({ submitting, error: null, userError: null })
  }

  setLoginError = error => {
    this.setState({ submitting: false, error, userError: null })
  }

  setLoginUserError = userError => {
    this.setState({ submitting: false, error: null, userError })
  }

  handleLogin = ({ email, password }) => {
    const { dev, reload } = this.props

    this.setLoginSubmitting(true)

    return this.props.loginUser({ username: email, password }, reload)
      .then(this.props.onLoginSuccess)
      .catch((err) => {

        this.setLoginSubmitting(false)

        if (err.user) {
          // there was a problem reloading user
          // the user is probably still logged in
          if (dev) {
            err.url = parse(err.config.url, true)
            this.setLoginUserError(err)
          } else {
            this.setLoginError('unknown')
          }
        } else if (err
          && err.response
          && err.response.data
          && err.response.data.error
          && err.response.data.error === 'access_denied'
        ) {
          throw new SubmissionError({ _error: err.response.data.error })
        } else {
          this.setLoginError('unknown')
        }
      })
  }

  handleClickSignup = (evt) => {
    const { onClickSignup, signUp } = this.props
    evt.preventDefault()
    signUp('sign_up_auth')
    if (onClickSignup) { onClickSignup(evt) }
  }

  handleClickForgotPassword = (evt) => {
    const { onClickForgotPassword } = this.props
    evt.preventDefault()
    if (onClickForgotPassword) { onClickForgotPassword(evt) }
  }

  getErrorMessage = (code) => {
    const messages = {
      'unknown': (<FormattedMessage
        id='auth.login.error.unknown'
        defaultMessage='An unknown error has occured. Please try again.'
      />),
    }

    if (code in messages) {
      return messages[code]
    }
  }

  render () {
    const { appConfig, leftAlign, dev } = this.props
    const { submitting, error, userError } = this.state

    return (
      <div className={styles.container}>

        <ReactCSSTransitionGroup
          component='div'
          transitionName='fade'
          transitionEnterTimeout={300}
          transitionLeaveTimeout={300}
        >
          {submitting ?
            <div key='loader'>
              <div className={styles.loader}>
                <Loader height={50} width={50} />
              </div>
            </div>
          : null}
        </ReactCSSTransitionGroup>

        <a
          href='/auth/signup'
          onClick={this.handleClickSignup}
          className={classnames(styles.alternateLink, {
            [styles.leftAlign]: leftAlign,
          })}
        >
          <FormattedMessage
            id='auth.login.sign_up'
            defaultMessage='Sign up'
          />
        </a>

        {appConfig.isChina ?
          <WechatLoginButton
            disabled={submitting}
            onLogin={this.handleLogin}
          />
        :
          <FacebookLoginButton
            disabled={submitting}
            onClickSignup={this.props.onClickSignup}
            onLoginSuccess={this.props.onLoginSuccess}
            reload={this.props.reload}
            setLoginError={this.setLoginError}
            setLoginSubmitting={this.setLoginSubmitting}
          />}

        <div className={styles.or}>
          <span className={styles.hr} />
          <span className={styles.orText}>
            <FormattedMessage id='auth.login.or' defaultMessage='or' />
          </span>
          <span className={styles.hr} />
        </div>

        {error ?
          <div className={styles.error}>
            {this.getErrorMessage(error)}
          </div>
        : null}

        <LoginForm
          loggingIn={submitting}
          onSubmit={this.handleLogin}
          onClickForgotPassword={this.handleClickForgotPassword}
        />

        {/*
          DEBUG information, only shown in local/devint
          Useful because DEVINT endpoints are often troublesome
        */}
        {userError && dev ?
          <div className={styles.userError}>
            <div className={styles.userErrorDescription}>
              You were logged in successfully, however there was an error fetching your user data.
              Feel free to try again, or just proceed to <a href="/my_home">my home</a>.
            </div>
            <div className={styles.userErrorMessage}>
              {userError.toString()} <br />
              {userError.response.status === 502 && 'Bad Gateway, server may be down or restarting'}
              {userError.response.status === 503 && 'Service Unavailable, server may be down or restarting'}
              {userError.response.status === 504 && 'Gateway Timeout, server took too long'}
            </div>
            <div><b>Host: </b>{userError.url.host}</div>
            <div><b>Path: </b>{userError.url.pathname}</div>
          </div>
        : null}

      </div>
    )
  }

}

export default connect(mapStateToProps, mapDispatchToActions)(Login)

